import { Link, useNavigate, useLocation } from "react-router-dom";
import { Badge } from "../../components/Badge";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_COMMISSARY_ORDERS } from "./queries";
import { useOrganizationStore, organizationStore } from "../../store/organization";
import { eventStore, useEventStore } from "../../store/event";
import { GET_LIST_EVENTS_BY_ORGANIZATION } from "../../queries/event";
import { CommissaryOrder } from "../../types/order-commissary";
import { useEffect, useRef, useState, useMemo } from "react";
import moment from "moment/moment";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  ColDef,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { usdFormat } from "../../utils";
import Loader from "../../components/Loader";

const client = createApolloClient();
const { getState: getEventStore } = eventStore;
const { getState: getOrgState } = organizationStore;

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "In Progress", value: "in_progress" },
  { label: "Ready", value: "ready" },
  { label: "Fulfilled", value: "fulfilled" },
  { label: "Rejected", value: "rejected" },
];

const statusBadgeColor = (status: string) => {
  switch (status) {
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "confirmed":
      return "bg-blue-100 text-blue-800";
    case "in_progress":
      return "bg-indigo-100 text-indigo-800";
    case "ready":
      return "bg-sky-100 text-sky-800";
    case "fulfilled":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const formatStatus = (status: string) =>
  status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export const CommissaryOrdersList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef<GridApi>();
  const currentOrgId = useOrganizationStore((state) => state.organizationId);
  const currentEventId = useEventStore((state) => state.eventId);

  const [rowData, setRowData] = useState<CommissaryOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const statusFilter = new URLSearchParams(location.search).get("status") || "";

  useEffect(() => {
    let isMounted = true;

    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        const eventId = getEventStore().eventId;
        const organizationId = getOrgState().organizationId;

        let where: any = {};

        // Scope to selected event, or all events for the org
        if (eventId) {
          where.event_id = { _eq: eventId };
        } else if (organizationId) {
          const { data: eventsData } = await client.query({
            query: GET_LIST_EVENTS_BY_ORGANIZATION,
            variables: { where: { organization_id: { _eq: organizationId } } },
            fetchPolicy: "network-only",
          });
          const eventIds = (eventsData?.events || []).map((e: any) => e.id);
          if (eventIds.length > 0) {
            where.event_id = { _in: eventIds };
          }
        }

        if (statusFilter) {
          where.status = { _eq: statusFilter };
        }

        const { data } = await client.query({
          query: GET_COMMISSARY_ORDERS,
          variables: { where },
          fetchPolicy: "network-only",
        });

        if (isMounted) {
          setRowData(data.order_commissary ?? []);
        }
      } catch (error) {
        console.error("Error fetching commissary orders:", error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchOrders();
    return () => { isMounted = false; };
  }, [location.search, currentOrgId, currentEventId]);

  const colDefs: ColDef<CommissaryOrder>[] = useMemo(
    () => [
      {
        headerName: "Order #",
        valueGetter: (p) => p.data?.id,
        cellRenderer: (params: { value: number; data: CommissaryOrder }) => (
          <Link to={`/commissary-orders/${params.data.id}`}>
            <Badge>{params.value}</Badge>
          </Link>
        ),
        filter: "agTextColumnFilter",
        pinned: "left" as const,
      },
      {
        headerName: "Vendor",
        valueGetter: (p) => p.data?.vendor?.name ?? "",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Status",
        valueGetter: (p) => p.data?.status ?? "",
        cellRenderer: (params: { value: string }) => (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeColor(
              params.value
            )}`}
          >
            {formatStatus(params.value)}
          </span>
        ),
        filter: "agTextColumnFilter",
        filterValueGetter: (p) => formatStatus(p.data?.status ?? ""),
      },
      {
        headerName: "Items",
        valueGetter: (p) => {
          const items = p.data?.items ?? [];
          return items.map((i) => `${i.quantity}x ${i.item_name}`).join(", ");
        },
        filter: "agTextColumnFilter",
        flex: 2,
      },
      {
        headerName: "Subtotal",
        valueGetter: (p) => p.data?.subtotal ?? 0,
        valueFormatter: (p) => usdFormat.format((p.value ?? 0) / 100),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total",
        valueGetter: (p) => p.data?.total ?? 0,
        valueFormatter: (p) => usdFormat.format((p.value ?? 0) / 100),
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Vendor Notes",
        valueGetter: (p) => p.data?.notes ?? "",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Organizer Notes",
        valueGetter: (p) => p.data?.organizer_notes ?? "",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "SMS Notify",
        valueGetter: (p) => (p.data?.sms_notify ? "Yes" : "No"),
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Created",
        valueGetter: (p) => p.data?.created_at,
        valueFormatter: (p) => {
          const date = p.value ? moment(p.value) : null;
          return date ? date.format("MM/DD/YYYY h:mm a") : "";
        },
        filter: "agTextColumnFilter",
        filterValueGetter: (p) =>
          p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm a") : "",
      },
      {
        headerName: "Updated",
        valueGetter: (p) => p.data?.updated_at,
        valueFormatter: (p) => {
          const date = p.value ? moment(p.value) : null;
          return date ? date.format("MM/DD/YYYY h:mm a") : "";
        },
        filter: "agTextColumnFilter",
        filterValueGetter: (p) =>
          p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm a") : "",
      },
    ],
    []
  );

  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `CommissaryOrders_${timestamp}.csv`,
        columnSeparator: ",",
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };

  return (
    <div
      className="w-full flex flex-col overflow-hidden"
      style={{ height: "calc(100vh - 128px)" }}
    >
      <div className="grid grid-cols-12 items-center gap-3 min-w-0 py-2">
        <div className="col-span-4 flex items-center gap-2">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() =>
                navigate(
                  opt.value
                    ? `/commissary-orders/list?status=${opt.value}`
                    : `/commissary-orders/list`
                )
              }
              className={`px-3 py-1.5 text-sm rounded-full border whitespace-nowrap ${
                statusFilter === opt.value
                  ? "bg-primary-600 text-white border-primary-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="col-span-8 flex justify-end items-center gap-2">
          <SecondaryButton onClick={handleExportCSV}>
            Export to CSV
          </SecondaryButton>
          <PrimaryButton
            onClick={() => navigate("/commissary-orders/create")}
          >
            Create Order
          </PrimaryButton>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <div className="ag-theme-quartz w-full h-full min-w-0">
          {isLoading ? (
            <Loader />
          ) : (
            <AgGridReact
              rowData={rowData}
              columnDefs={colDefs}
              rowHeight={rowHeight}
              autoSizeStrategy={autoSizeStrategy}
              pagination
              paginationPageSize={50}
              onGridReady={(params) => {
                gridRef.current = params.api;
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
